import {
  Badge,
  Box,
  Button,
  ButtonGroup,
  Card,
  Flex,
  HStack,
  Heading,
  Link,
  Spinner,
  Text,
  VStack,
  useClipboard,
  useColorModeValue,
} from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { RiCheckLine } from "@remixicon/react";
import { useMemo } from "react";
import { Helmet } from "react-helmet-async";
import { FormProvider, useForm } from "react-hook-form";
import { Link as RouterLink } from "react-router";
import { useLocalStorage } from "usehooks-ts";

import { Restricted } from "@/app/components/Restricted";
import { useOnboardingProgress } from "@/app/hooks/useOnboardingProgress";
import { SupportedHighlightLanguage } from "@/common/components/CodeBlock";
import { CodeExample, CodeExampleType } from "@/common/components/CodeExample";
import { FormRootError } from "@/common/components/Form/FormRootError";
import FormSubmit from "@/common/components/Form/FormSubmit";
import { ManagedFormControl } from "@/common/components/Form/ManagedFormControl";
import { RouterLinkButton } from "@/common/components/RouterLinkButton";
import { useEnvironment } from "@/common/contexts/EnvironmentContext";
import { useFormMutationSubmitHandler } from "@/common/hooks/useApiForm";
import { GithubRepositoryAutocompleteSelect } from "@/integration/components/github/GithubRepositoryAutocompleteSelect";
import { useGithubSaveRepoMutation } from "@/integration/data/useGithubSaveRepoMutation";
import useGithubConnectionUrl from "@/integration/hooks/useGithubConnectionUrl";
import useLinearInitiateConnectionUrl from "@/integration/hooks/useLinearInitiateConnectionUrl";
import useSlackConnectionUrl from "@/integration/hooks/useSlackConnectionUrl";
import {
  GithubIntegrationSchema,
  GithubIntegrationType,
} from "@reflag/shared/githubConnectionAPI";
import { GlobalSettingsUrl, NewFeatureUrl } from "@reflag/shared/urls";

type SdkSnippet = {
  id: "react" | "vue" | "browser" | "node";
  label: string;
  language: SupportedHighlightLanguage;
  code: string;
};

function buildSdkSnippets(
  publishableKey: string,
  secretKey: string,
): SdkSnippet[] {
  return [
    {
      id: "react",
      label: "React",
      language: "jsx",
      code: `import { ReflagProvider } from "@reflag/react-sdk";

function App() {
  return (
    <ReflagProvider
      publishableKey="${publishableKey}"
      user={{ id: "john_doe", name: "John Doe" }}
      company={{ id: "acme_inc", name: "Acme Inc." }}
    >
      <YourApp />
    </ReflagProvider>
  );
}`,
    },
    {
      id: "browser",
      label: "Browser",
      language: "javascript",
      code: `import { ReflagClient } from "@reflag/browser-sdk";

const client = new ReflagClient({
  publishableKey: "${publishableKey}",
  user: { id: "john_doe", name: "John Doe" },
  company: { id: "acme_inc", name: "Acme Inc." },
});

await client.initialize();`,
    },
    {
      id: "vue",
      label: "Vue.js",
      language: "vue",
      code: `<script setup>
import { onMounted } from "vue";
import { ReflagClient } from "@reflag/browser-sdk";

const reflagClient = new ReflagClient({
  publishableKey: "${publishableKey}",
  user: { id: "john_doe", name: "John Doe" },
  company: { id: "acme_inc", name: "Acme Inc." },
});

onMounted(async () => {
  await reflagClient.initialize();
});
</script>

<template>
  <YourApp />
</template>`,
    },
    {
      id: "node",
      label: "Node.js",
      language: "javascript",
      code: `import { ReflagClient } from "@reflag/node-sdk";

const client = new ReflagClient({
  secretKey: "${secretKey}",
});

await client.initialize();`,
    },
  ];
}

function buildAiPrompt(snippet: SdkSnippet): string {
  const promptLanguage =
    snippet.id === "react" ? "tsx" : snippet.id === "vue" ? "vue" : "ts";

  return `We want to integrate the Reflag SDK into our app using this ${snippet.label} example.

Note:
- Keep existing app behavior and structure.
- Initialize the SDK exactly once.
- The publishableKey/secretKey from the snippet below is for development

Snippet:
\`\`\`${promptLanguage}
${snippet.code}
\`\`\`

---

Docs: https://docs.reflag.com`;
}

function StepCard({
  title,
  description,
  status,
  children,
  opacity = 1,
  isCompleted = false,
  isActive = false,
}: {
  title: string;
  description: string;
  status: React.ReactNode;
  children?: React.ReactNode;
  opacity?: number;
  isCompleted?: boolean;
  isActive?: boolean;
}) {
  const cardBg = useColorModeValue(
    isActive ? "#F7EBDE" : "white",
    isActive ? "black" : "gray.850",
  );
  const cardBorderWidth = useColorModeValue(isActive ? "0px" : "1px", "1px");
  const cardBorderColor = useColorModeValue(
    isCompleted ? "green.200" : isActive ? "transparent" : "gray.200",
    isCompleted
      ? "rgba(103, 171, 131, 0.45)"
      : isActive
        ? "orange.200"
        : "whiteAlpha.200",
  );
  const titleColor = useColorModeValue(
    undefined,
    isActive ? "orange.200" : undefined,
  );

  return (
    <Box
      bg={cardBg}
      border="1px solid"
      borderColor={cardBorderColor}
      borderWidth={cardBorderWidth}
      borderRadius="2xl"
      opacity={opacity}
      px={{ base: 4, md: 5 }}
      py={{ base: 4, md: 5 }}
      transition="opacity 0.2s ease-in-out, border-color 0.2s ease-in-out, background-color 0.2s ease-in-out"
    >
      <Flex direction="column" gap={4}>
        <Flex align="flex-start" justify="space-between">
          <Box>
            <Heading color={titleColor} size="sm">
              {title}
            </Heading>
            <Text color="dimmed" fontSize="sm" mt={1}>
              {description}
            </Text>
          </Box>
          {status}
        </Flex>
        {children}
      </Flex>
    </Box>
  );
}

function PendingStatusBadge({ children }: { children: React.ReactNode }) {
  const bg = useColorModeValue("white", undefined);
  const color = useColorModeValue("orange.700", undefined);

  return (
    <Badge
      bg={bg}
      color={color}
      colorScheme={useColorModeValue(undefined, "orange")}
    >
      {children}
    </Badge>
  );
}

type IntegrationStepStatusProps = {
  title: string;
  description: string;
  completed: boolean;
  connected: boolean;
  skipped: boolean;
  connectHref: string;
  connectLabel?: string;
  showConnectButton?: boolean;
  opacity?: number;
  isActive?: boolean;
  onSkip: () => void;
  onUnskip: () => void;
  children?: React.ReactNode;
};

function IntegrationStepStatus({
  title,
  description,
  completed,
  connected,
  skipped,
  connectHref,
  connectLabel,
  showConnectButton = !completed,
  opacity = 1,
  isActive = false,
  onSkip,
  onUnskip,
  children,
}: IntegrationStepStatusProps) {
  return (
    <StepCard
      description={description}
      status={
        completed ? (
          <Badge colorScheme={connected ? "green" : "gray"}>
            <HStack spacing={1}>
              <RiCheckLine size={14} />
              <Text>{connected ? "Connected" : "Skipped"}</Text>
            </HStack>
          </Badge>
        ) : (
          <PendingStatusBadge>Pending</PendingStatusBadge>
        )
      }
      title={title}
      opacity={opacity}
      isCompleted={completed}
      isActive={isActive}
    >
      {!connected && (
        <ButtonGroup size="sm" variant="outline">
          {showConnectButton && !completed && (
            <Restricted role="admin">
              <Button as="a" href={connectHref}>
                {connectLabel ?? "Connect"}
              </Button>
            </Restricted>
          )}
          {!connected &&
            (!skipped ? (
              <Button onClick={onSkip}>Skip for now</Button>
            ) : (
              <Button onClick={onUnskip}>Undo skip</Button>
            ))}
        </ButtonGroup>
      )}
      {children}
    </StepCard>
  );
}

function GithubRepositorySetup({ repository }: { repository: string | null }) {
  const form = useForm<GithubIntegrationType>({
    resolver: zodResolver(GithubIntegrationSchema),
    mode: "onChange",
    defaultValues: {
      repository: repository ?? "",
    },
  });
  const saveRepoMutation = useGithubSaveRepoMutation();
  const handleSubmit = useFormMutationSubmitHandler(
    form,
    saveRepoMutation,
    undefined,
    {
      successToast: "GitHub repository saved",
      errorToast: "Failed to save GitHub repository",
    },
  );

  return (
    <FormProvider {...form}>
      <VStack
        align="flex-start"
        as="form"
        spacing={3}
        w="full"
        onSubmit={handleSubmit}
      >
        <Box maxW="sm" w="full">
          <ManagedFormControl
            label="Repository"
            name="repository"
            render={({ field }) => (
              <GithubRepositoryAutocompleteSelect
                bg="appBackground"
                value={field.value}
                valueAsId
                onChange={field.onChange}
              />
            )}
          />
        </Box>
        <FormRootError errors={form.formState.errors} />
        <Restricted role="admin">
          <ButtonGroup size="sm" variant="outline">
            <FormSubmit
              checkForDirty={false}
              isLoading={saveRepoMutation.isPending}
            >
              Save repository
            </FormSubmit>
          </ButtonGroup>
        </Restricted>
      </VStack>
    </FormProvider>
  );
}

export default function Onboarding() {
  const { env } = useEnvironment();
  const activeTextColor = useColorModeValue("green.700", "green.200");

  const { menuProgress, steps, skipOptionalStep, unskipOptionalStep } =
    useOnboardingProgress({ forceFullStatus: true });
  const slackConnectionUrl = useSlackConnectionUrl();
  const githubConnectionUrl = useGithubConnectionUrl();
  const linearConnectionUrl = useLinearInitiateConnectionUrl();

  const snippets = useMemo(
    () =>
      buildSdkSnippets(env.sdkAccess.publishableKey, env.sdkAccess.secretKey),
    [env.sdkAccess.publishableKey, env.sdkAccess.secretKey],
  );
  const [activeSnippetId, setActiveSnippetId] = useLocalStorage<
    SdkSnippet["id"]
  >("onboarding-code-example-language", "react");
  const activeSnippet =
    snippets.find((snippet) => snippet.id === activeSnippetId) ?? snippets[0];
  const codeExamples = useMemo<CodeExampleType[]>(
    () =>
      snippets.map((snippet) => ({
        key: snippet.id,
        label: snippet.label,
        highlighterLanguage: snippet.language,
        docsSection:
          snippet.id === "react"
            ? "supported-languages/react-sdk"
            : snippet.id === "node"
              ? "supported-languages/node-sdk"
              : "supported-languages/browser-sdk",
        code: snippet.code,
      })),
    [snippets],
  );
  const aiPrompt = useMemo(() => buildAiPrompt(activeSnippet), [activeSnippet]);
  const { onCopy: onCopyPrompt } = useClipboard(aiPrompt);

  const stepCompletion = [
    steps.codeIntegration.completed,
    steps.slack.completed,
    steps.github.completed,
    steps.linear.completed,
    steps.createFlag.completed,
  ];
  const nextIncompleteStepIndex = stepCompletion.findIndex((isDone) => !isDone);
  const isStepActive = (stepIndex: number) =>
    nextIncompleteStepIndex !== -1 && stepIndex === nextIncompleteStepIndex;
  const stepOpacity = (stepIndex: number) =>
    nextIncompleteStepIndex === -1 || stepIndex <= nextIncompleteStepIndex
      ? 1
      : 0.5;

  return (
    <Flex
      direction="column"
      gap={4}
      maxW="900px"
      px={{ base: 4, md: 6 }}
      py={{ base: 4, md: 5 }}
      w="full"
    >
      <Helmet>
        <title>Onboarding</title>
      </Helmet>
      <Box>
        <Heading size="md">
          Onboarding ({menuProgress.completed}/{menuProgress.total})
        </Heading>
        <Text color="dimmed" mt={1}>
          Let&apos;s get you set up! This only takes a few minutes.
        </Text>
      </Box>

      <StepCard
        description="Install Reflag in your application using an SDK"
        status={
          steps.codeIntegration.completed ? (
            <Badge colorScheme="green">
              <HStack spacing={1}>
                <RiCheckLine size={14} />
                <Text>Detected</Text>
              </HStack>
            </Badge>
          ) : (
            <PendingStatusBadge>Waiting</PendingStatusBadge>
          )
        }
        title="1. Set up code integration"
        opacity={stepOpacity(0)}
        isCompleted={steps.codeIntegration.completed}
        isActive={isStepActive(0)}
      >
        <Card direction="row">
          <CodeExample
            examples={codeExamples}
            feature={{ id: "onboarding", key: "onboarding" }}
            copyForAILabel="Copy for AI"
            initialExample="react"
            languageStorageKey="onboarding-code-example-language"
            linearMarkdown={aiPrompt}
            linearTitle="Implement Reflag"
            onCopyForAI={onCopyPrompt}
            px={0}
            py={1}
            sx={{
              ".prism-code-editor": {
                backgroundColor: "transparent",
                borderColor: "transparent",
                borderWidth: "0px",
              },
            }}
            languageSelectMinW="24"
            topActionsRight={2}
            topActionsSize="2xs"
            topActionsSpacing={1}
            topActionsTop={2}
            showCopyForAIButton
            showOpenInSandboxButton={false}
            onLanguageChange={(key) =>
              setActiveSnippetId(key as SdkSnippet["id"])
            }
          />
        </Card>

        {steps.codeIntegration.completed &&
        steps.codeIntegration.latestActivity ? (
          <HStack color={activeTextColor} fontSize="sm" spacing={2}>
            <RiCheckLine size={16} />
            <Text>Installed</Text>
          </HStack>
        ) : (
          <HStack color="dimmed" fontSize="sm" spacing={2}>
            <Spinner size="xs" speed="1.8s" />
            <Text>Waiting for activity.</Text>
          </HStack>
        )}
      </StepCard>

      <IntegrationStepStatus
        completed={steps.slack.completed}
        connected={steps.slack.connected}
        connectHref={slackConnectionUrl}
        description="Send notifications to Slack."
        opacity={stepOpacity(1)}
        isActive={isStepActive(1)}
        skipped={steps.slack.skipped}
        title="2. Set up Slack integration"
        onSkip={() => skipOptionalStep("slack")}
        onUnskip={() => unskipOptionalStep("slack")}
      />

      <IntegrationStepStatus
        completed={steps.github.completed}
        connected={steps.github.connected}
        connectHref={githubConnectionUrl}
        description="Enable GitHub repository integration for cleanup workflows."
        opacity={stepOpacity(2)}
        isActive={isStepActive(2)}
        skipped={steps.github.skipped}
        showConnectButton={!steps.github.connected}
        title="3. Set up GitHub integration"
        onSkip={() => skipOptionalStep("github")}
        onUnskip={() => unskipOptionalStep("github")}
      >
        {steps.github.connected && !steps.github.completed ? (
          <GithubRepositorySetup repository={steps.github.repository} />
        ) : null}
      </IntegrationStepStatus>

      <IntegrationStepStatus
        completed={steps.linear.completed}
        connected={steps.linear.connected}
        connectHref={linearConnectionUrl}
        description="Connect Linear for issue/project workflows."
        opacity={stepOpacity(3)}
        isActive={isStepActive(3)}
        skipped={steps.linear.skipped}
        title="4. Set up Linear integration"
        onSkip={() => skipOptionalStep("linear")}
        onUnskip={() => unskipOptionalStep("linear")}
      />

      <StepCard
        description="Create your first flag or import flags to finish onboarding."
        status={
          steps.createFlag.completed ? (
            <Badge colorScheme="green">
              <HStack spacing={1}>
                <RiCheckLine size={14} />
                <Text>Completed</Text>
              </HStack>
            </Badge>
          ) : (
            <PendingStatusBadge>Pending</PendingStatusBadge>
          )
        }
        title="5. Create flag or import flags"
        opacity={stepOpacity(4)}
        isCompleted={steps.createFlag.completed}
        isActive={isStepActive(4)}
      >
        <Restricted role="contributor">
          <HStack spacing={4}>
            <RouterLinkButton
              isDisabled={steps.createFlag.completed}
              to={NewFeatureUrl(env)}
            >
              {steps.createFlag.completed ? "Flag created" : "Create flag"}
            </RouterLinkButton>
            <Link
              as={RouterLink}
              color={steps.createFlag.completed ? "dimmed" : undefined}
              pointerEvents={steps.createFlag.completed ? "none" : undefined}
              to={GlobalSettingsUrl(env, "app-import")}
            >
              Import flags
            </Link>
          </HStack>
        </Restricted>
      </StepCard>
    </Flex>
  );
}
